import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useBrokerPayments, useBrokers } from "services/hooks/rentals";
import { currencyFormatter } from "utilities/currencyFormatter";

const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const formatMoney = (value) => {
  if (value == null || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return currencyFormatter(num);
};

const brokerNameFromRow = (row) =>
  row.related_broker_landlord?.related_broker?.broker_name ||
  row.broker_name ||
  row.related_broker?.broker_name ||
  "—";

const landlordNameFromRow = (row) => {
  const landlord = row.related_broker_landlord?.related_landlord;
  if (!landlord) return row.landlord_name || "—";
  return (
    landlord.business_name ||
    `${landlord.related_user?.first_name || ""} ${landlord.related_user?.last_name || ""}`.trim() ||
    "—"
  );
};

const BrokerCommissionPayouts = () => {
  const role = useSelector((state) => state.role);
  const isManager = role === "manager" || role === "admin";

  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [brokerFilterId, setBrokerFilterId] = useState("");
  const [paidFilter, setPaidFilter] = useState("false");
  const [message, setMessage] = useState({ type: "", text: "" });
  const [markOpen, setMarkOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [markForm, setMarkForm] = useState({ date_paid: "", description: "" });

  const { brokers, isLoadingBrokers } = useBrokers();
  const {
    brokerPayments,
    isLoadingBrokerPayments,
    errorBrokerPayments,
    isSubmittingBrokerPayment,
    markCommissionPaid,
  } = useBrokerPayments({
    brokerId: brokerFilterId ? Number(brokerFilterId) : undefined,
    month: month ? Number(month) : undefined,
    year: year ? Number(year) : undefined,
    commissionPaid: paidFilter === "" ? undefined : paidFilter === "true",
  });

  const years = useMemo(() => {
    const current = new Date().getFullYear();
    return [current - 1, current, current + 1];
  }, []);

  const openMarkPaid = (row) => {
    setSelectedRow(row);
    setMarkForm({
      date_paid: new Date().toISOString().slice(0, 10),
      description: "",
    });
    setMarkOpen(true);
  };

  const handleMarkPaid = async () => {
    if (!selectedRow?.id || !markForm.date_paid) {
      setMessage({ type: "error", text: "Payment date is required." });
      return;
    }
    const result = await markCommissionPaid(selectedRow.id, markForm);
    if (result.success) {
      if (result.pendingApproval) {
        setMessage({
          type: "info",
          text: result.message || "Mark paid submitted for approval.",
        });
      } else {
        setMessage({ type: "success", text: "Commission marked as paid." });
      }
      setMarkOpen(false);
      setSelectedRow(null);
    } else {
      setMessage({ type: "error", text: result.message });
    }
  };

  if (!isManager) {
    return (
      <Box m={2}>
        <Alert severity="warning">Only Property Manager or Admin can manage broker commission payouts.</Alert>
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
        Broker Commission Payouts
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Review broker commissions and mark them paid when settled. Defaults to unpaid items needing action.
      </Typography>

      {message.text && (
        <Alert
          severity={
            message.type === "error" ? "error" : message.type === "info" ? "info" : "success"
          }
          sx={{ mb: 2 }}
          onClose={() => setMessage({ type: "", text: "" })}
        >
          {message.text}
        </Alert>
      )}

      <Box display="flex" flexWrap="wrap" gap={2} mb={3}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Month</InputLabel>
          <Select label="Month" value={month} onChange={(e) => setMonth(e.target.value)}>
            <MenuItem value="">All months</MenuItem>
            {MONTHS.map((m) => (
              <MenuItem key={m.value} value={String(m.value)}>
                {m.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Year</InputLabel>
          <Select label="Year" value={year} onChange={(e) => setYear(e.target.value)}>
            <MenuItem value="">All years</MenuItem>
            {years.map((y) => (
              <MenuItem key={y} value={String(y)}>
                {y}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Broker</InputLabel>
          <Select label="Broker" value={brokerFilterId} onChange={(e) => setBrokerFilterId(e.target.value)}>
            <MenuItem value="">All brokers</MenuItem>
            {(brokers || []).map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.broker_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={paidFilter} onChange={(e) => setPaidFilter(e.target.value)}>
            <MenuItem value="false">Unpaid (action needed)</MenuItem>
            <MenuItem value="true">Paid</MenuItem>
            <MenuItem value="">All</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {errorBrokerPayments && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorBrokerPayments}
        </Alert>
      )}

      <TableContainer component={Paper}>
        {isLoadingBrokerPayments || isLoadingBrokers ? (
          <Box p={4} display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Broker</TableCell>
                <TableCell>Landlord</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Base</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Date paid</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {brokerPayments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>No commission records for this filter.</TableCell>
                </TableRow>
              ) : (
                brokerPayments.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{brokerNameFromRow(row)}</TableCell>
                    <TableCell>{landlordNameFromRow(row)}</TableCell>
                    <TableCell align="right">{formatMoney(row.amount)}</TableCell>
                    <TableCell>
                      {(row.commission_base || row.related_broker_landlord?.commission_base || "gross").toUpperCase()}
                    </TableCell>
                    <TableCell>
                      {row.commission_paid ? (
                        <Chip size="small" label="Paid" color="success" variant="outlined" />
                      ) : (
                        <Chip size="small" label="Unpaid" color="warning" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>{row.date_paid || "—"}</TableCell>
                    <TableCell align="right">
                      {!row.commission_paid && (
                        <Button size="small" variant="contained" onClick={() => openMarkPaid(row)}>
                          Mark paid
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      <Dialog open={markOpen} onClose={() => setMarkOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Mark commission as paid</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            type="date"
            label="Date paid"
            InputLabelProps={{ shrink: true }}
            value={markForm.date_paid}
            onChange={(e) => setMarkForm((p) => ({ ...p, date_paid: e.target.value }))}
            required
          />
          <TextField
            fullWidth
            margin="normal"
            label="Description"
            value={markForm.description}
            onChange={(e) => setMarkForm((p) => ({ ...p, description: e.target.value }))}
            placeholder="e.g. Paid via bank transfer"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleMarkPaid} disabled={isSubmittingBrokerPayment}>
            {isSubmittingBrokerPayment ? <CircularProgress size={22} /> : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BrokerCommissionPayouts;
